import { z } from "zod";

/** Fixed chart of accounts used by the journal (FR-ACC-04). Sub-accounts carry the specific party or wallet. */
export const ACCOUNTS = {
  cash: "1010",
  receivable: "1100",
  inventory: "1200",
  vatInput: "1300",
  payable: "2000",
  vatOutput: "2100",
  sdPayable: "2110",
  /** What is owed to (or, when in debit, carried forward from) NBR after a filed return. */
  vatPayable: "2120",
  openingEquity: "3000",
  ownerCapital: "3100",
  ownerDrawings: "3200",
  sales: "4000",
  salesReturns: "4010",
  otherIncome: "4900",
  rounding: "4910",
  cogs: "5000",
  inventoryLoss: "5100",
  expenses: "6000",
  paymentFees: "6100",
} as const;
export type AccountCode = (typeof ACCOUNTS)[keyof typeof ACCOUNTS];

export type AccountType = "asset" | "liability" | "equity" | "income" | "expense";

/** Name and type of each account, for the trial balance, balance sheet and ledgers. */
export const ACCOUNT_INFO: Record<AccountCode, { type: AccountType; en: string; bn: string }> = {
  "1010": { type: "asset", en: "Cash, mobile banking and bank", bn: "নগদ, মোবাইল ব্যাংকিং ও ব্যাংক" },
  "1100": { type: "asset", en: "Customer dues (receivable)", bn: "গ্রাহকের বাকি (পাওনা)" },
  "1200": { type: "asset", en: "Stock at cost", bn: "মজুদ (ক্রয়মূল্যে)" },
  "1300": {
    type: "asset",
    en: "VAT paid on purchases (input)",
    bn: "ক্রয়ে পরিশোধিত ভ্যাট (উপকরণ কর)",
  },
  "2000": { type: "liability", en: "Supplier dues (payable)", bn: "সরবরাহকারীর পাওনা" },
  "2100": {
    type: "liability",
    en: "VAT collected on sales (output)",
    bn: "বিক্রয়ে আদায়কৃত ভ্যাট (উৎপাদ কর)",
  },
  "2110": { type: "liability", en: "Supplementary duty collected", bn: "আদায়কৃত সম্পূরক শুল্ক" },
  "2120": {
    type: "liability",
    en: "VAT and SD payable to NBR",
    bn: "এনবিআর-এর কাছে পরিশোধযোগ্য ভ্যাট ও সম্পূরক শুল্ক",
  },
  "3000": { type: "equity", en: "Opening balances", bn: "প্রারম্ভিক জের" },
  "3100": { type: "equity", en: "Owner's capital", bn: "মালিকের মূলধন" },
  "3200": { type: "equity", en: "Owner's drawings", bn: "মালিকের উত্তোলন" },
  "4000": { type: "income", en: "Sales", bn: "বিক্রয়" },
  "4010": { type: "income", en: "Sales returns", bn: "বিক্রয় ফেরত" },
  "4900": { type: "income", en: "Other income", bn: "অন্যান্য আয়" },
  "4910": { type: "income", en: "Rounding", bn: "রাউন্ডিং" },
  "5000": { type: "expense", en: "Cost of goods sold", bn: "বিক্রীত পণ্যের ব্যয়" },
  "5100": { type: "expense", en: "Stock losses", bn: "মজুদ ক্ষতি" },
  "6000": { type: "expense", en: "Expenses", bn: "খরচ" },
  "6100": { type: "expense", en: "Payment and courier charges", bn: "পেমেন্ট ও কুরিয়ার চার্জ" },
};

export function isAccountCode(value: string): value is AccountCode {
  return value in ACCOUNT_INFO;
}

/** A VAT tax period: one calendar month, "YYYY-MM" (Mushak 9.1). */
export const vatPeriodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use YYYY-MM")
  .refine((v) => v >= "2019-07", "Periods start from July 2019 (VAT and SD Act 2012)");

export const vatReturnFileSchema = z.object({
  /** The NBR e-return submission number, once filed online. */
  submissionRef: z.string().trim().max(60).optional(),
  note: z.string().trim().max(300).optional(),
});

export const vatPaymentSchema = z.object({
  accountId: z.string().min(1).max(64),
  amount: z.number().int().positive().max(10_000_000_000_000),
  /** Treasury challan or e-payment reference. */
  challanNo: z.string().trim().min(3).max(60),
  paidAt: z.number().int().positive().optional(),
});
export type VatPayment = z.output<typeof vatPaymentSchema>;
